/**
 * Rating Summary Block
 * Aggregate star rating display with breakdown bars.
 */

import { registerBlockRenderer } from '../registry';
import { createBlockWrapper } from '../utils';
import { iconStar, iconStarEmpty, iconStarHalf } from '../../icons';

// =============================================================================
// TYPES
// =============================================================================

interface RatingBreakdown {
  stars: number;
  count: number;
}

interface RatingSummaryData {
  average: number;
  total: number;
  breakdown?: RatingBreakdown[];
  title?: string;
}

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidRatingSummaryData(data: unknown): data is RatingSummaryData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // Must have average and total
  if (typeof d.average !== 'number') return false;
  if (typeof d.total !== 'number') return false;

  // Validate breakdown if present
  if (d.breakdown !== undefined) {
    if (!Array.isArray(d.breakdown)) return false;
    for (const item of d.breakdown) {
      if (!item || typeof item !== 'object') return false;
      const b = item as Record<string, unknown>;
      if (typeof b.stars !== 'number') return false;
      if (typeof b.count !== 'number') return false;
    }
  }

  return true;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function renderStars(rating: number): string {
  let html = '';
  for (let i = 1; i <= 5; i++) {
    if (i <= Math.floor(rating)) {
      html += `<span class="pms-rating-star pms-filled">${iconStar()}</span>`;
    } else if (i - 0.5 <= rating) {
      html += `<span class="pms-rating-star pms-half">${iconStarHalf()}</span>`;
    } else {
      html += `<span class="pms-rating-star">${iconStarEmpty()}</span>`;
    }
  }
  return html;
}

// =============================================================================
// RENDER FUNCTION
// =============================================================================

function renderRatingSummary(data: RatingSummaryData, container: HTMLElement): void {
  const wrapper = createBlockWrapper('rating-summary');

  const average = Math.min(5, Math.max(0, data.average));
  const total = data.total;

  let html = '<div class="pms-rating-summary">';

  // Header with overall rating
  html += `
    <div class="pms-rating-summary-header">
      <div class="pms-rating-summary-overall">
        <span class="pms-rating-summary-score">${average.toFixed(1)}</span>
        <span class="pms-rating-summary-max">/5</span>
        <div class="pms-rating-summary-stars">${renderStars(average)}</div>
        <div class="pms-rating-summary-count">${total.toLocaleString()} reviews</div>
      </div>
  `;

  // Breakdown bars
  if (data.breakdown && data.breakdown.length > 0) {
    html += '<div class="pms-rating-summary-breakdown">';

    // Sort by stars descending (5 to 1)
    const sortedBreakdown = [...data.breakdown].sort((a, b) => b.stars - a.stars);

    // Create a map for quick lookup
    const breakdownMap = new Map(sortedBreakdown.map(b => [b.stars, b.count]));

    // Always show 5 to 1 stars
    for (let stars = 5; stars >= 1; stars--) {
      const count = breakdownMap.get(stars) || 0;
      const percent = total > 0 ? (count / total) * 100 : 0;

      html += `
        <div class="pms-rating-breakdown-row">
          <span class="pms-rating-breakdown-label">${stars} star${stars !== 1 ? 's' : ''}</span>
          <div class="pms-rating-breakdown-bar">
            <div class="pms-rating-breakdown-fill" style="width: ${percent}%"></div>
          </div>
          <span class="pms-rating-breakdown-count">${count.toLocaleString()}</span>
        </div>
      `;
    }

    html += '</div>';
  }

  html += '</div></div>';

  wrapper.innerHTML = html;
  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('rating-summary', (data: unknown, container: HTMLElement) => {
  if (!isValidRatingSummaryData(data)) {
    console.warn('[rating-summary] Invalid data:', data);
    const wrapper = createBlockWrapper('rating-summary');
    wrapper.innerHTML = '<div class="pms-block-error">Invalid rating data - requires average and total</div>';
    container.appendChild(wrapper);
    return;
  }

  renderRatingSummary(data, container);
});
